import { NewsPost } from '../lib/supabase';
import { Calendar, Download } from 'lucide-react';

interface CategorySectionProps {
  title: string;
  posts: NewsPost[];
  onNavigateToArticle: (id: string) => void;
  layout?: 'grid' | 'featured' | 'list';
}

export default function CategorySection({ title, posts, onNavigateToArticle, layout = 'grid' }: CategorySectionProps) {
  if (posts.length === 0) return null;

  if (layout === 'featured' && posts.length > 0) {
    const featured = posts[0];
    const others = posts.slice(1, 4);

    return (
      <section className="mb-16">
        <h2 className="font-horizon text-3xl md:text-4xl text-red-500 mb-8 pb-4 border-b-2 border-red-900/50">
          {title}
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <article
            onClick={() => onNavigateToArticle(featured.id)}
            className="bg-gradient-to-br from-red-900/20 to-black border-2 border-red-600/40 rounded-lg overflow-hidden hover:border-red-500 hover:-translate-y-1 transition-all duration-300 cursor-pointer group lg:col-span-1 lg:row-span-2"
          >
            <div className="relative h-80 lg:h-full overflow-hidden">
              <img
                src={featured.image_url || 'https://images.pexels.com/photos/442576/pexels-photo-442576.jpeg'}
                alt={featured.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center text-gray-300 text-xs mb-3">
                  <Calendar size={14} className="mr-2" />
                  {new Date(featured.published_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </div>
                <h3 className="font-horizon text-2xl text-red-400 mb-3 group-hover:text-red-300 transition-colors">
                  {featured.title}
                </h3>
                <p className="text-gray-300 text-sm mb-4 line-clamp-2">
                  {featured.excerpt}
                </p>
                {featured.author && (
                  <div className="flex items-center gap-2">
                    {featured.author_image_url && (
                      <img
                        src={featured.author_image_url}
                        alt={featured.author}
                        className="w-8 h-8 rounded-full object-cover border border-red-500/50"
                      />
                    )}
                    <span className="text-xs text-gray-400">{featured.author}</span>
                  </div>
                )}
              </div>
            </div>
          </article>

          <div className="space-y-4">
            {others.map((post) => (
              <article
                key={post.id}
                onClick={() => onNavigateToArticle(post.id)}
                className="bg-gradient-to-br from-red-900/10 to-black border border-red-600/30 rounded-lg overflow-hidden hover:border-red-500/70 hover:-translate-y-1 transition-all duration-300 cursor-pointer group flex"
              >
                <div className="relative w-32 h-32 flex-shrink-0 overflow-hidden">
                  <img
                    src={post.image_url || 'https://images.pexels.com/photos/442576/pexels-photo-442576.jpeg'}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-4 flex-1">
                  <div className="flex items-center text-gray-400 text-xs mb-2">
                    <Calendar size={12} className="mr-1" />
                    {new Date(post.published_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </div>
                  <h3 className="font-horizon text-sm text-red-400 mb-1 group-hover:text-red-300 transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-gray-300 text-xs line-clamp-2">
                    {post.excerpt}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'list') {
    return (
      <section className="mb-16">
        <h2 className="font-horizon text-3xl md:text-4xl text-red-500 mb-8 pb-4 border-b-2 border-red-900/50">
          {title}
        </h2>
        <div className="space-y-4">
          {posts.map((post) => (
            <article
              key={post.id}
              onClick={() => onNavigateToArticle(post.id)}
              className="bg-gradient-to-br from-red-900/10 to-black border border-red-600/30 rounded-lg overflow-hidden hover:border-red-500/70 hover:-translate-y-1 transition-all duration-300 cursor-pointer group flex"
            >
              <div className="relative w-48 h-32 flex-shrink-0 overflow-hidden">
                <img
                  src={post.image_url || 'https://images.pexels.com/photos/442576/pexels-photo-442576.jpeg'}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-5 flex-1">
                <div className="flex items-center text-gray-400 text-xs mb-2">
                  <Calendar size={14} className="mr-2" />
                  {new Date(post.published_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </div>
                <h3 className="font-horizon text-lg text-red-400 mb-2 group-hover:text-red-300 transition-colors line-clamp-1">
                  {post.title}
                </h3>
                <p className="text-gray-300 text-sm line-clamp-2 mb-3">
                  {post.excerpt}
                </p>
                {post.author && (
                  <div className="flex items-center gap-2">
                    {post.author_image_url && (
                      <img
                        src={post.author_image_url}
                        alt={post.author}
                        className="w-6 h-6 rounded-full object-cover border border-red-500/30"
                      />
                    )}
                    <span className="text-xs text-gray-400">{post.author}</span>
                  </div>
                )}
                {post.download_links && Array.isArray(post.download_links) && post.download_links.length > 0 && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-red-900/30">
                    <Download size={14} className="text-yellow-400" />
                    <span className="text-yellow-400 text-xs font-bold">{post.download_links.length} Free Downloads</span>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="mb-16">
      <h2 className="font-horizon text-3xl md:text-4xl text-red-500 mb-8 pb-4 border-b-2 border-red-900/50">
        {title}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {posts.map((post) => (
          <article
            key={post.id}
            onClick={() => onNavigateToArticle(post.id)}
            className="bg-gradient-to-br from-red-900/10 to-black border border-red-600/30 rounded-lg overflow-hidden hover:border-red-500/70 hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
          >
            <div className="relative h-48 overflow-hidden">
              <img
                src={post.image_url || 'https://images.pexels.com/photos/442576/pexels-photo-442576.jpeg'}
                alt={post.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
            </div>
            <div className="p-5">
              <div className="flex items-center text-gray-400 text-xs mb-3">
                <Calendar size={14} className="mr-2" />
                {new Date(post.published_at).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </div>
              <h3 className="font-horizon text-lg text-red-400 mb-2 group-hover:text-red-300 transition-colors line-clamp-2 leading-snug">
                {post.title}
              </h3>
              <p className="text-gray-300 text-sm line-clamp-3 mb-4">
                {post.excerpt}
              </p>
              {post.author && (
                <div className="flex items-center gap-2 mb-3 pb-3 border-b border-red-900/30">
                  {post.author_image_url && (
                    <img
                      src={post.author_image_url}
                      alt={post.author}
                      className="w-8 h-8 rounded-full object-cover border border-red-500/30"
                    />
                  )}
                  <span className="text-xs text-gray-400">{post.author}</span>
                </div>
              )}
              <div className="flex items-center text-yellow-400 font-semibold group-hover:text-yellow-300 transition-colors text-sm">
                Read More →
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
